import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  RobotIcon,
  FacebookLogoIcon,
  ChatCircleDotsIcon,
  PaperPlaneTiltIcon,
  ShieldCheckIcon,
  PlugIcon,
  SlidersIcon,
  LightningIcon,
  EnvelopeSimpleIcon,
  ArrowRightIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const features = [
  {
    icon: PaperPlaneTiltIcon,
    title: "Automated Replies",
    copy: "Instantly respond to Instagram comments using pre-defined keywords to boost engagement and never miss a lead.",
  },
  {
    icon: ChatCircleDotsIcon,
    title: "Smart DM Routing",
    copy: "Send automated private messages to users who interact with your posts, ensuring 24/7 customer support at scale.",
  },
  {
    icon: ShieldCheckIcon,
    title: "Anti-Spam Variants",
    copy: "Rotate up to 3 randomized message variations per keyword with cooldown rate-limiting to keep interactions organic and account-safe.",
  },
];

const steps = [
  {
    num: "01",
    icon: PlugIcon,
    title: "Connect",
    copy: "Securely link your Instagram Business account via Facebook Login. Only accounts you explicitly authorize are enabled.",
  },
  {
    num: "02",
    icon: SlidersIcon,
    title: "Configure",
    copy: "Set matching keywords and your desired auto-responses. Choose public reply text, private DM content, and target posts.",
  },
  {
    num: "03",
    icon: LightningIcon,
    title: "Automate",
    copy: "Megatron monitors your comments and DMs in real-time via the official Instagram Graph API and responds within seconds.",
  },
];

export default function Login() {
  const { user, loading } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const u = new URLSearchParams(window.location.search);
    const e = u.get("error");
    const detail = u.get("detail");
    const meta = u.get("meta");
    if (e) {
      const errorMessages = {
        oauth_failed_code_exchange: "Facebook rejected the login code. See Meta's response below.",
        oauth_failed_long_lived_token: "Facebook accepted login but couldn't issue a long-lived token. See Meta's response below.",
        oauth_failed_fetch_me: "Facebook issued a token but we couldn't fetch your profile. See Meta's response below.",
        invalid_state: "Your login session expired. Please try again.",
        missing_params: "Facebook didn't return the expected parameters. Try again.",
      };
      let msg = errorMessages[e] || `${e.replace(/_/g, " ")}${detail ? ` (HTTP ${detail})` : ""}`;
      if (meta) {
        try {
          const parsed = JSON.parse(decodeURIComponent(meta));
          const fbErr = parsed?.error || parsed;
          msg += `\n\nMeta said: ${fbErr.message || JSON.stringify(fbErr)}`;
        } catch {
          msg += `\n\nMeta said: ${decodeURIComponent(meta)}`;
        }
      }
      setErr(msg);
    }
    if (!loading && user) window.location.href = "/";
  }, [user, loading]);

  const onLogin = async () => {
    setBusy(true);
    try {
      const { authorization_url } = await api.getAuthUrl();
      window.location.href = authorization_url;
    } catch (e) {
      toast.error("Could not start login");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="login-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-900 px-6 py-20 sm:py-28">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(245,158,11,0.12),transparent_50%)]" />
        <div className="relative mx-auto grid max-w-6xl gap-16 lg:grid-cols-2 lg:items-center">
          <div>
            {/* Logo + Powered by */}
            <div className="mb-10 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-amber-500 text-zinc-950">
                <RobotIcon size={28} weight="fill" />
              </div>
              <div>
                <p className="font-display text-2xl font-black uppercase tracking-tight text-white">
                  Megatron
                </p>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-500" data-testid="powered-by">
                  Powered by yoga.rubika
                </p>
              </div>
            </div>

            <h1 className="font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
              Automate your<br />
              Instagram. Scale<br />
              your engagement.
            </h1>
            <p className="mt-6 max-w-lg text-base text-zinc-400 leading-relaxed">
              Megatron is a marketing-automation SaaS that lets businesses connect their
              Instagram Business accounts and auto-respond to comments and DMs with
              keyword-matched public replies and private messages — fully powered by the
              official Instagram Graph API. Set it up in under 30 seconds.
            </p>

            {err && (
              <div
                className="mt-6 rounded-sm border border-red-800 bg-red-950/50 p-3 text-sm text-red-300 whitespace-pre-line"
                data-testid="login-error"
              >
                {err}
              </div>
            )}

            <button
              onClick={onLogin}
              disabled={busy}
              data-testid="fb-login-btn"
              className="mt-10 inline-flex w-full max-w-sm items-center justify-center gap-3 rounded-sm bg-amber-500 px-8 py-4 text-base font-bold tracking-wide text-zinc-950 transition-all duration-150 hover:-translate-y-0.5 hover:bg-amber-400 disabled:opacity-50"
            >
              <FacebookLogoIcon size={20} weight="fill" />
              {busy ? "Redirecting…" : "Continue with Facebook"}
            </button>
            <p className="mt-4 max-w-sm text-xs text-zinc-600">
              By continuing you agree to our{" "}
              <a href="/privacy" className="text-amber-500 hover:underline" data-testid="hero-privacy-link">
                Privacy Policy
              </a>{" "}
              and{" "}
              <a href="/data-deletion" className="text-amber-500 hover:underline" data-testid="hero-data-deletion-link">
                Data Deletion
              </a>{" "}
              terms.
            </p>
          </div>

          {/* Right: visual / quick stats */}
          <div className="hidden lg:block">
            <div className="relative rounded-sm border border-zinc-800 bg-zinc-900/50 p-6 backdrop-blur">
              <div className="space-y-4">
                <div className="flex items-start gap-3 rounded-sm border border-zinc-800 bg-zinc-950 p-4">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-500 text-[11px] font-black text-zinc-950">
                    01
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      Incoming comment
                    </p>
                    <p className="mt-1 text-sm text-zinc-300">
                      <span className="font-bold text-white">@jane_doe</span> — "Would love to try this, how do I get the guide?"
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-sm border border-amber-900/40 bg-amber-950/20 p-4">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-500 text-zinc-950">
                    <PaperPlaneTiltIcon size={14} weight="fill" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-amber-600">
                      Public reply · 1.2s
                    </p>
                    <p className="mt-1 text-sm text-zinc-300">
                      "Sent you a DM with the full guide 📬"
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-sm border border-emerald-900/40 bg-emerald-950/10 p-4">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500 text-zinc-950">
                    <ChatCircleDotsIcon size={14} weight="fill" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-500">
                      Private DM · 1.4s
                    </p>
                    <p className="mt-1 text-sm text-zinc-300">
                      "Hey Jane! Here's your guide: https://…"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-b border-zinc-900 px-6 py-20" data-testid="features-section">
        <div className="mx-auto max-w-6xl">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-amber-500">
            Product
          </p>
          <h2 className="mt-2 font-display text-3xl font-black tracking-tight text-white sm:text-4xl">
            What Megatron does.
          </h2>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <article
                key={f.title}
                data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="group rounded-sm border border-zinc-800 bg-zinc-900/30 p-6 transition-all hover:-translate-y-1 hover:border-amber-500/50"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-amber-500/10 text-amber-500 group-hover:bg-amber-500 group-hover:text-zinc-950">
                  <f.icon size={22} weight="duotone" />
                </div>
                <h3 className="mt-4 font-display text-xl font-bold tracking-tight text-white">
                  {f.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                  {f.copy}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b border-zinc-900 px-6 py-20" data-testid="how-it-works-section">
        <div className="mx-auto max-w-6xl">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-amber-500">
            Workflow
          </p>
          <h2 className="mt-2 font-display text-3xl font-black tracking-tight text-white sm:text-4xl">
            How it works.
          </h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {steps.map((s, i) => (
              <div
                key={s.num}
                data-testid={`step-${s.num}`}
                className="relative rounded-sm border border-zinc-800 bg-zinc-900/30 p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-zinc-950 text-amber-500">
                    <s.icon size={22} weight="duotone" />
                  </div>
                  <span className="font-display text-3xl font-black text-zinc-800">
                    {s.num}
                  </span>
                </div>
                <h3 className="mt-4 font-display text-xl font-bold tracking-tight text-white">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                  {s.copy}
                </p>
                {i < steps.length - 1 && (
                  <ArrowRightIcon
                    size={20}
                    weight="bold"
                    className="absolute right-[-14px] top-1/2 hidden -translate-y-1/2 text-amber-500 md:block"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-b border-zinc-900 px-6 py-16">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-6 text-center">
          <h2 className="font-display text-3xl font-black tracking-tight text-white sm:text-4xl">
            Ready to automate?
          </h2>
          <p className="max-w-lg text-sm text-zinc-400">
            Connect your Facebook Page in under 30 seconds. No credit card. Turn off any time.
          </p>
          <button
            onClick={onLogin}
            disabled={busy}
            data-testid="cta-fb-login-btn"
            className="inline-flex items-center justify-center gap-3 rounded-sm bg-amber-500 px-8 py-4 text-base font-bold tracking-wide text-zinc-950 transition-all duration-150 hover:-translate-y-0.5 hover:bg-amber-400 disabled:opacity-50"
          >
            <FacebookLogoIcon size={20} weight="fill" />
            {busy ? "Redirecting…" : "Continue with Facebook"}
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-6 py-12" data-testid="landing-footer">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col items-start justify-between gap-8 border-b border-zinc-900 pb-10 md:flex-row md:items-center">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-amber-500 text-zinc-950">
                <RobotIcon size={22} weight="fill" />
              </div>
              <div>
                <p className="font-display text-lg font-black uppercase tracking-tight text-white">
                  Megatron
                </p>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                  by yoga.rubika
                </p>
              </div>
            </div>
            <nav className="flex flex-wrap items-center gap-2">
              <a
                href="/privacy"
                data-testid="footer-privacy"
                className="inline-flex items-center gap-2 rounded-sm border border-zinc-800 bg-zinc-900 px-4 py-2 text-xs font-bold uppercase tracking-wider text-zinc-200 hover:border-amber-500 hover:text-amber-500"
              >
                <ShieldCheckIcon size={14} weight="duotone" /> Privacy Policy
              </a>
              <a
                href="/data-deletion"
                data-testid="footer-data-deletion"
                className="inline-flex items-center gap-2 rounded-sm border border-zinc-800 bg-zinc-900 px-4 py-2 text-xs font-bold uppercase tracking-wider text-zinc-200 hover:border-amber-500 hover:text-amber-500"
              >
                <ShieldCheckIcon size={14} weight="duotone" /> Data Deletion
              </a>
              <a
                href="mailto:connect@yogarubika.com?subject=Megatron%20Support"
                data-testid="footer-contact"
                className="inline-flex items-center gap-2 rounded-sm border border-zinc-800 bg-zinc-900 px-4 py-2 text-xs font-bold uppercase tracking-wider text-zinc-200 hover:border-amber-500 hover:text-amber-500"
              >
                <EnvelopeSimpleIcon size={14} weight="duotone" /> Contact Support
              </a>
            </nav>
          </div>
          <p className="mt-8 max-w-3xl text-xs leading-relaxed text-zinc-500" data-testid="footer-disclaimer">
            Megatron is a marketing automation platform by yoga.rubika. We use the official
            Instagram Graph API to provide secure and compliant engagement tools for
            businesses. Megatron is not affiliated with, endorsed by, or sponsored by Meta
            Platforms, Inc. Instagram and Facebook are trademarks of Meta Platforms, Inc.
          </p>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-zinc-700">
            © {new Date().getFullYear()} yoga.rubika · All rights reserved
          </p>
        </div>
      </footer>
    </div>
  );
}
